import classNames from 'classnames';
import React from 'react';

export interface ButtonProps extends Omit<React.ButtonHTMLAttributes<HTMLElement>, 'type'> {
  active?: boolean;
  'aria-label'?: string;
  block?: boolean;
  color?: string;
  outline?: boolean;
  tag?: React.ElementType;
  innerRef?: React.Ref<HTMLElement>;
  ref?: React.Ref<HTMLElement>;
  size?: string;
  close?: boolean;
  type?: string;
  href?: string;
  [key: string]: any;
}

const Button = (props: ButtonProps) => {
  const {
    active,
    'aria-label': ariaLabel,
    block,
    className,
    close,
    color = 'secondary',
    outline,
    size,
    tag,
    innerRef,
    ref,
    onClick,
    disabled,
    children,
    ...attributes
  } = props;

  let Tag = (tag as React.ElementType) || 'button';

  const handleClick = (e: React.MouseEvent<HTMLElement>) => {
    if (disabled) {
      e.preventDefault();
      return;
    }
    if (onClick) {
      onClick(e as React.MouseEvent<HTMLButtonElement>);
    }
  };

  let renderedChildren = children;
  if (close && typeof children === 'undefined') {
    renderedChildren = <span aria-hidden>×</span>;
  }

  const btnOutlineColor = `btn${outline ? '-outline' : ''}-${color}`;

  const classes = classNames(
    className,
    { close },
    close || 'btn',
    close || btnOutlineColor,
    size ? `btn-${size}` : false,
    block ? 'btn-block' : false,
    { active, disabled }
  );

  if ((attributes as { href?: string }).href && Tag === 'button') {
    Tag = 'a';
  }

  const defaultAriaLabel = close ? 'Close' : null;
  const computedType = Tag === 'button' && onClick ? 'button' : (props.type as string | undefined);

  return (
    <Tag
      type={computedType}
      {...attributes}
      disabled={disabled}
      className={classes}
      ref={innerRef ?? ref}
      onClick={handleClick}
      aria-label={ariaLabel || defaultAriaLabel || undefined}
    >
      {renderedChildren}
    </Tag>
  );
};

Button.displayName = 'Button';

export default Button;
